import { kulaApiFetch } from './kulaApiFetch.js'
import { ethers } from 'ethers'
import KulaDaoKIPGovernorAbi from '../../../contracts/KulaDaoKipGovernor.abi.js'
import RegistryAbi from '../../../contracts/Registry.abi.js'
import { unpackV1ProposalPayload } from '../../../enterprise/utilities/proposal.js'
import { isV1ProposalPayload } from '../../../enterprise/utilities/proposal.js'

function _getContract({ governorAddress, abiMethods }) {
  return new ethers.Contract(
    governorAddress,
    abiMethods,
    new ethers.providers.Web3Provider(window.ethereum).getSigner()
  )
}

/** @param {KipConfig} body */
export async function createKip(body) {
  return await kulaApiFetch('/v1/contracts/kip', {
    method: 'POST',
    body,
  })
}

/** @param {string} deployId */
export async function getKipStatus(deployId) {
  return await kulaApiFetch(
    `/v1/contracts/kip/deploy/status?deployid=${deployId}`,
    {
      method: 'GET',
    }
  )
}

export async function getKips() {
  const response = await kulaApiFetch('/v1/contracts/kip', {
    method: 'GET',
  })
  return await response.json()
}

export async function getUserKipRoles(
  governorAddress,
  walletAddress,
  retryCount = 10,
  retryDelay = 1000
) {
  let roles = []

  if (!governorAddress || !ethers.utils.isAddress(governorAddress)) {
    return roles
  }

  for (let attempt = 0; attempt <= retryCount; attempt++) {
    try {
      const contract = _getContract({
        governorAddress: governorAddress,
        abiMethods: KulaDaoKIPGovernorAbi,
      })

      const executiveRole = await contract.EXECUTIVE_ROLE()
      const adminRole = await contract.ADMIN_ROLE()
      const ownerRole = await contract.OWNER_ROLE()
      const courtRole = await contract.COURT_ROLE()

      const hasExecutiveRole = await contract.hasRole(
        executiveRole,
        walletAddress
      )
      if (hasExecutiveRole) {
        roles.push('EXECUTIVE_ROLE')
      }

      const hasAdminRole = await contract.hasRole(adminRole, walletAddress)
      if (hasAdminRole) {
        roles.push('ADMIN_ROLE')
      }

      const hasOwnerRole = await contract.hasRole(ownerRole, walletAddress)
      if (hasOwnerRole) {
        roles.push('OWNER_ROLE')
      }

      const hasCourtRole = await contract.hasRole(courtRole, walletAddress)
      if (hasCourtRole) {
        roles.push('COURT_ROLE')
      }

      return roles
    } catch (error) {
      if (attempt < retryCount) {
        await new Promise((resolve) => setTimeout(resolve, retryDelay))
        retryDelay *= 1.5
      } else {
        console.error(
          'Error fetching kip roles after multiple attempts:',
          error
        )
      }
    }
  }

  return roles
}

export async function getKipProposals(governorAddress) {
  try {
    const response = await kulaApiFetch(
      `/v1/contracts/kip/proposals?address=${governorAddress}`,
      {
        method: 'GET',
      }
    )
    const data = await response.json()

    return data.map((proposal, index) => {
      if (isV1ProposalPayload(proposal.description)) {
        return {
          ...proposal,
          ...unpackV1ProposalPayload(proposal.description),
        }
      }
      return proposal
    })
  } catch (error) {
    console.error('Error fetching KIP proposals:', error)
    return []
  }
}

export async function getManualProposalState(governorAddress, proposalId) {
  const contract = _getContract({
    governorAddress: governorAddress,
    abiMethods: KulaDaoKIPGovernorAbi,
  })

  try {
    return Number(await contract.proposalState(proposalId))
  } catch (error) {
    console.error('Error fetching proposal state:', error)
    throw error
  }
}

export async function createKipProposal(proposalInput) {
  const gasLimit = 3000000
  try {
    const proposalDescription =
      typeof proposalInput === 'string'
        ? proposalInput
        : proposalInput.proposalDescription

    if (!proposalDescription) {
      throw new Error('Proposal description is required')
    }

    if (!window.Globals?.kip?.governorAddress) {
      throw new Error(
        'KIP governor address not found. Please ensure you are connected to the correct network.'
      )
    }

    let governorAddress
    if (typeof window.Globals.kip.governorAddress === 'object') {
      const addresses = Object.values(window.Globals.kip.governorAddress)
      governorAddress = addresses.length > 0 ? String(addresses[0]) : null
    } else {
      governorAddress = String(window.Globals.kip.governorAddress)
    }

    if (!governorAddress || !ethers.utils.isAddress(governorAddress)) {
      throw new Error(`Invalid governor address: ${governorAddress}`)
    }

    const governorContract = _getContract({
      governorAddress: governorAddress,
      abiMethods: KulaDaoKIPGovernorAbi,
    })

    const transaction = await governorContract.propose(proposalDescription, {
      gasLimit,
    })
    const receipt = await transaction.wait()

    const proposalCreatedEvent = receipt.events?.find(
      (event) => event.event === 'ProposalCreated'
    )

    const proposalId = proposalCreatedEvent?.args[0]?.toString()

    if (!proposalId) {
      throw new Error('Failed to extract proposal ID from transaction receipt')
    }

    return proposalId
  } catch (error) {
    console.error('Error creating KIP proposal:', error)
    throw error
  }
}

export async function getProposal(governorAddress, proposalId) {
  const contract = _getContract({
    governorAddress: governorAddress,
    abiMethods: KulaDaoKIPGovernorAbi,
  })

  try {
    const proposal = await contract.getProposal(proposalId)

    return {
      id: proposal.id.toString(),
      proposer: proposal.proposer,
      executiveCount: Number(proposal.executiveCount),
      courtCount: Number(proposal.courtCount),
      description: proposal.description,
      created: Number(proposal.created),
      executiveVoteStart: Number(proposal.executiveVoteStart),
      timeLockStart: Number(proposal.timeLockStart),
      timeLockEnd: Number(proposal.timeLockEnd),
      executiveYesCount: Number(proposal.executiveYesCount),
      executiveNoCount: Number(proposal.executiveNoCount),
      courtYesCount: Number(proposal.courtYesCount),
      courtNoCount: Number(proposal.courtNoCount),
      state: Number(proposal.state),
    }
  } catch (error) {
    console.error('Error fetching proposal:', error)
    throw error
  }
}

export async function voteOnProposal(governorAddress, proposalId, vote) {
  const contract = _getContract({
    governorAddress: governorAddress,
    abiMethods: KulaDaoKIPGovernorAbi,
  })
  const gasLimit = 6000000
  await contract.vote(proposalId, vote, { gasLimit })
}

export async function userHasVoted(governorAddress, walletAddress, proposalId) {
  const contract = _getContract({
    governorAddress: governorAddress,
    abiMethods: KulaDaoKIPGovernorAbi,
  })
  const hasVoted = await contract.hasVoted(walletAddress, proposalId)
  return hasVoted
}

export async function executeKipProposal(governorAddress, proposalId) {
  const contract = _getContract({
    governorAddress: governorAddress,
    abiMethods: KulaDaoKIPGovernorAbi,
  })
  await contract.execute(proposalId)
}
